import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type { PromocodeErrorCode } from '@/types/promocode';
import type { PlatformFlags } from '~/utils/apostatudo/platform-gate';
import { redeemPromocodeHandler } from '~/handlers/redeem-promocode.handler';
import { usePlayerProfileStore } from '~/stores/player-profile';
import { GAMIFICATION_SLUGS } from '~/types/player-profile';

export type PromocodeNotificationType = 'success' | 'error' | 'warning';

// 'ready' = o formulário de resgate. 'ineligible' = conta abaixo do nível CRM
// mínimo da gamificação (`gamifications.promocodes.is_eligible: false` em
// /players/me): sobe o modal de aviso de nível no lugar do formulário, do mesmo
// jeito que a roletinha e o check-in fazem.
//
// Não há 'loading' nem 'error' aqui: diferente das outras duas features, abrir o
// promocode não busca nada no core — o único request é o POST do resgate, cuja
// falha já é tratada pela notificação.
export type PromocodeStatus = 'ready' | 'ineligible';

export interface PromocodeNotification {
  is_open: boolean;
  type: PromocodeNotificationType;
  title: string;
  message: string;
}

export const usePromocodeStore = defineStore('promocode', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  const playerProfileStore = usePlayerProfileStore();

  const isRedeeming = ref(false);

  /* ---------------- modal global ---------------- */

  // Abertura do modal (gatilho via store, sem navegar de rota): menu lateral,
  // card do hub, deep-link `?open=promocode` e a própria rota /promocode.
  const isOpen = ref(false);
  const status = ref<PromocodeStatus>('ready');

  // Código que entra no input já preenchido (deep-link `?promocode=XXX`, lido
  // pela página e pelo middleware do cupom). Vive na store, e não no componente,
  // porque quem conhece o código é quem MANDA abrir — o modal só o consome.
  const prefillCode = ref('');

  // Mesma regra da roletinha e do check-in: a promoção é de quem chegou ao nível
  // mínimo dela. Só o veredito FRESCO do core nega (`isKnownIneligibleFor`) —
  // num "não sei" (perfil em trânsito, requisição falhada, core offline, mapa
  // quebrado) o modal abre normal, porque negar o resgate por falta de resposta
  // tiraria o bônus de quem tem direito a ele. Quem dá a palavra final continua
  // sendo o core, no POST do resgate (`not_eligible`).
  const isKnownIneligible = computed(() =>
    playerProfileStore.isKnownIneligibleFor(GAMIFICATION_SLUGS.promocodes),
  );

  function open(options?: { code?: string }) {
    // Deslogado não abre o modal: só o login. O resgate exige auth de qualquer
    // forma, e o formulário por cima da tela de login só atrapalharia.
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    // Abertura pedida PELO JOGADOR começa limpa (ENG-824). O `reset()` só roda no
    // `@after-leave` do modal, então ele cobre "aberto → fechado" mas não um bloco
    // guardado SEM modal aberto — o resgate pela barra do perfil oficial. Sem isto a
    // próxima abertura pelo menu subiria a parede por cima de um formulário vazio,
    // falando de um código que o jogador nunca digitou ali.
    clearPlatformBlock();

    prefillCode.value = options?.code?.trim() ?? '';
    status.value = isKnownIneligible.value ? 'ineligible' : 'ready';
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  function reset() {
    status.value = 'ready';
    prefillCode.value = '';
    // A parede de um código recusado morre junto com a sessão do modal (ENG-824).
    // Sem isto ela voltaria montada na PRÓXIMA abertura, por cima de um formulário
    // vazio, falando de um código que o jogador ainda nem digitou.
    clearPlatformBlock();
  }

  const notification = ref<PromocodeNotification>({
    is_open: false,
    type: 'success',
    title: '',
    message: '',
  });

  const NOTIFICATION_TITLES: Record<PromocodeNotificationType, string> = {
    success: 'Código resgatado com sucesso',
    error: 'Não foi possível resgatar',
    warning: 'Promocode já resgatado',
  };

  // Códigos que são estado esperado do usuário (não falha) — exibidos
  // como aviso (amarelo) em vez de erro (vermelho).
  const WARNING_ERROR_CODES: PromocodeErrorCode[] = ['already_redeemed'];

  function getNotificationType(
    error: string | null,
  ): Exclude<PromocodeNotificationType, 'success'> {
    return isKnownErrorCode(error) && WARNING_ERROR_CODES.includes(error)
      ? 'warning'
      : 'error';
  }

  function openNotification(type: PromocodeNotificationType, message: string) {
    notification.value = {
      is_open: true,
      type,
      title: NOTIFICATION_TITLES[type],
      message,
    };
  }

  function closeNotification() {
    notification.value.is_open = false;
  }

  /**
   * As superfícies em que o código recusado VALE (ENG-824). `null` = nenhuma recusa de
   * plataforma nesta sessão do modal, e a parede não monta.
   *
   * Vive aqui e não na resposta porque a parede sobrevive ao clique: ela fica na tela
   * até o jogador digitar outro código.
   */
  const platformBlockedFlags = ref<PlatformFlags | null>(null);

  function clearPlatformBlock() {
    platformBlockedFlags.value = null;
  }

  /*
   * A parede ANTES DE DIGITAR, quando a regra GLOBAL já nega a feature inteira
   * (ENG-824). Aqui o `/players/me` basta: com a global ligada, ela vale para todos os
   * códigos, e mandar o jogador digitar para ouvir "não dá" seria trabalho perdido.
   *
   * Isso NÃO substitui a recusa do servidor: com a global desligada quem manda é a flag
   * de CADA código, e só o core sabe qual código é qual — aquele caminho continua sendo
   * o principal.
   */
  // Não é `useLayout()` porque aquele registra um watcher que mexe no menu lateral e,
  // numa store, nunca desmonta.
  const { isBlockedHere } = usePlatformGate();

  const featurePlatformFlags = computed(
    () =>
      playerProfileStore.gamifications?.[GAMIFICATION_SLUGS.promocodes]
        ?.platforms ?? null,
  );

  const isFeaturePlatformBlocked = computed(() =>
    isBlockedHere(featurePlatformFlags.value),
  );

  /** As flags que a parede deve desenhar: a do código recusado, senão a da feature. */
  const platformGateFlags = computed(
    () => platformBlockedFlags.value ?? featurePlatformFlags.value,
  );

  const isPlatformBlocked = computed(
    () => platformBlockedFlags.value !== null || isFeaturePlatformBlocked.value,
  );

  /**
   * Se a parede tem saída. A parede TROCA o formulário (o input desmonta atrás dela),
   * então quando ela veio de UM código recusado é preciso um caminho de volta — senão
   * o modal tranca para qualquer outro código até o jogador fechá-lo.
   *
   * Vindo da regra GLOBAL não há volta a oferecer: ali a feature inteira está fechada
   * nesta plataforma, e devolver o formulário só levaria à mesma recusa.
   */
  const canDismissPlatformBlock = computed(
    () =>
      platformBlockedFlags.value !== null && !isFeaturePlatformBlocked.value,
  );

  const UNAVAILABLE_MESSAGE =
    'Serviço indisponível no momento. Tente novamente.';

  const ERROR_MESSAGES: Record<PromocodeErrorCode, string> = {
    not_found: 'Promocode inválido.',
    inactive: 'Este promocode não está mais disponível.',
    not_started: 'Este promocode ainda não está disponível.',
    expired: 'Este promocode expirou.',
    out_of_stock: 'Este promocode atingiu o limite de usos.',
    already_redeemed: 'Você já resgatou este promocode.',
    not_eligible: 'Sua conta não é elegível para este promocode.',
    bonus_failed: 'Não foi possível aplicar o bônus. Tente novamente.',
  };

  function isKnownErrorCode(error: string | null): error is PromocodeErrorCode {
    return (
      error !== null &&
      Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, error)
    );
  }

  function getErrorMessage(
    status: number | null,
    error: string | null,
    message: string | null,
  ) {
    if (status === null) return UNAVAILABLE_MESSAGE;

    if (status === 429) {
      return 'Código esgotado';
    }

    // o core é a fonte de verdade do texto; o mapa local fica como fallback
    // pra quando a resposta não traz message (corpo ilegível, 200 fora do
    // contrato) ou pra um código novo ainda não mapeado.
    if (message?.trim()) return message.trim();

    if (isKnownErrorCode(error)) {
      return ERROR_MESSAGES[error];
    }

    return 'Promocode não aplicado.';
  }

  async function redeemPromocode(code: string): Promise<boolean> {
    // Enter no input ignora o :disabled do botão — sem este guard,
    // Enter repetido dispararia POSTs duplicados durante o resgate
    if (isRedeeming.value || !code.trim()) return false;

    if (!authStore.isAuthenticated) {
      openLogin();

      return false;
    }

    // Só o código vai no body: a identidade É O TOKEN. O core introspecta o
    // Bearer na Cactus (EnsureUserTokenIdentity) e resolve o jogador por ele —
    // `user_ext_id`, `user_email` e `user_cpf` saíram do RedeemRequest lá, e com
    // eles saíram as guardas locais de perfil carregado e CPF preenchido, que só
    // existiam pra alimentar esses campos.
    isRedeeming.value = true;

    try {
      const result = await redeemPromocodeHandler({
        code: code.trim().toUpperCase(),
      });

      if (result.ok) {
        openNotification('success', `Você recebeu: ${result.bonus.name}`);

        return true;
      }

      // PLATAFORMA (ENG-824): dentro do modal a resposta é a PAREDE, nunca a
      // notificação de erro do formulário — que é exatamente o que a feature existe
      // para evitar. A recusa é a única fonte das flags aqui (o código é digitado,
      // não listado), então elas ficam guardadas e o modal desenha a parede com elas.
      //
      // ESTA STORE NÃO ABRE MODAL. O resgate também chega de fora dele (a
      // `HighlightedGamesBar` do perfil oficial chama `redeemPromocode` direto), e
      // subir um modal que o jogador não pediu é pior que a recusa que ele pediu:
      // troca a tela onde ele estava. Fora do modal a recusa vira NOTIFICAÇÃO, com a
      // mensagem que o core mandou — o que fechava o buraco do botão morto era ter
      // resposta, não ter parede.
      if (result.error === 'platform_blocked') {
        if (result.platforms && isOpen.value) {
          platformBlockedFlags.value = result.platforms;

          return false;
        }

        // Fora do modal, ou 403 sem o bloco (inclusive um bloco torto, que o adapter
        // peneira). A parede NOMEIA as superfícies liberadas, então sem flags não há
        // parede para montar de todo jeito. Ela continua sendo estado ESPERADO do
        // jogador, e o retorno é daqui — antes da captura lá embaixo — para não virar
        // um evento no Sentry a cada tentativa.
        //
        // As flags NÃO são guardadas neste ramo: guardar sem modal aberto deixava a
        // próxima abertura subindo parede por cima de um formulário vazio, falando de
        // um código que o jogador nunca digitou ali.
        openNotification(
          getNotificationType(result.error),
          getErrorMessage(result.status, result.error, result.message),
        );

        return false;
      }

      openNotification(
        getNotificationType(result.error),
        getErrorMessage(result.status, result.error, result.message),
      );

      const isExpectedBusinessError =
        result.status !== null &&
        result.status < 500 &&
        (result.status === 429 ||
          result.status === 422 ||
          isKnownErrorCode(result.error));

      if (!isExpectedBusinessError) {
        // erros de negócio mapeados são esperados e seriam ruído na triagem;
        // só rede/timeout, 5xx e códigos desconhecidos interessam ao Sentry
        Sentry.captureException(
          result.cause ??
            new Error(`promocode redeem failed (status ${result.status})`),
          {
            tags: { source: 'promocode.redeem' },
            extra: { status: result.status, errorCode: result.error },
          },
        );
      }

      return false;
    } finally {
      isRedeeming.value = false;
    }
  }

  return {
    isOpen,
    status,
    prefillCode,
    isRedeeming,
    notification,
    open,
    close,
    reset,
    redeemPromocode,
    openNotification,
    closeNotification,
    platformGateFlags,
    isPlatformBlocked,
    canDismissPlatformBlock,
    clearPlatformBlock,
  };
});
